import { useMemo } from 'react';
import type { ParcelRecord } from '@/utils/parcelGeometry';

export type MemberKind = 'member' | 'non_member';

/** The subset of an FPA member bulk-list record this index needs. */
export interface MemberSearchSource {
  id: number;
  name?: string | null;
  ref_number?: string | null;
  member_type?: string | null;
  plot_number?: string | null;
  street?: string | null;
  area_suburb?: string | null;
}

export interface MemberSearchEntry {
  id: number;
  name: string;
  refNumber: string | null;
  memberType: MemberKind;
  plotNumber: string | null;
  street: string | null;
  areaSuburb: string | null;
  /** Resolved via the tenant's parcel-owner links, if this member has one
   *  and it's within the currently-loaded parcel set. Null otherwise —
   *  callers show the member row with no distance/pin-drop in that case. */
  parcel: ParcelRecord | null;
}

export interface MemberSearchMatch {
  entry: MemberSearchEntry;
  tier: number;
}

const normalize = (s: string): string => s.trim().toLowerCase();

/**
 * Resolves raw member rows (from the bulk member cache) + the tenant's
 * parcel-owner links into searchable entries, each carrying its linked
 * `ParcelRecord` when one exists in the currently-loaded parcel set.
 */
export function buildMemberSearchEntries(
  members: MemberSearchSource[],
  parcelRefByMemberId: ReadonlyMap<number, string>,
  refIndex: ReadonlyMap<string, ParcelRecord>,
): MemberSearchEntry[] {
  const entries: MemberSearchEntry[] = [];
  for (const m of members) {
    if (m.id == null || !m.name?.trim()) continue;
    const ref = parcelRefByMemberId.get(m.id);
    entries.push({
      id: m.id,
      name: m.name.trim(),
      refNumber: m.ref_number?.trim() || null,
      memberType: m.member_type === 'non_member' ? 'non_member' : 'member',
      plotNumber: m.plot_number?.trim() || null,
      street: m.street?.trim() || null,
      areaSuburb: m.area_suburb?.trim() || null,
      parcel: (ref && refIndex.get(ref)) || null,
    });
  }
  return entries;
}

/**
 * Tiered member search — name-first (tiers 1-3: exact/prefix/substring),
 * then ref_number (tier 4), then address fields (tier 5) as the fallback
 * "must also check their address" tier per the feature's requirement.
 *
 * `addressOnly` flips this for the location-search mode's "members at this
 * address" results: only address fields are checked (tier 1), so typing a
 * street name doesn't surface every member whose *name* happens to contain
 * those letters — a location search is about places, not people.
 */
export function searchMembers(
  entries: MemberSearchEntry[],
  rawQuery: string,
  opts: { addressOnly?: boolean; limit?: number } = {},
): MemberSearchMatch[] {
  const { addressOnly = false, limit = 20 } = opts;
  const query = rawQuery.trim();
  if (!query) return [];
  const needle = normalize(query);
  if (!needle) return [];

  const results: MemberSearchMatch[] = [];
  const seen = new Set<number>();
  const push = (entry: MemberSearchEntry, tier: number) => {
    if (seen.has(entry.id)) return;
    seen.add(entry.id);
    results.push({ entry, tier });
  };

  if (!addressOnly) {
    for (const e of entries) if (normalize(e.name) === needle) push(e, 1);
    for (const e of entries) if (normalize(e.name).startsWith(needle)) push(e, 2);
    for (const e of entries) if (normalize(e.name).includes(needle)) push(e, 3);
    for (const e of entries) {
      if (e.refNumber && normalize(e.refNumber).includes(needle)) push(e, 4);
    }
  }

  const addressTier = addressOnly ? 1 : 5;
  for (const e of entries) {
    const hay = [e.plotNumber, e.street, e.areaSuburb].filter(Boolean) as string[];
    if (hay.some((h) => normalize(h).includes(needle))) push(e, addressTier);
  }

  results.sort((a, b) => a.tier - b.tier || a.entry.name.localeCompare(b.entry.name));
  return results.slice(0, limit);
}

/** Memoized member search entries — rebuilds when any input changes. */
export function useMemberSearchIndex(
  members: MemberSearchSource[],
  parcelRefByMemberId: ReadonlyMap<number, string>,
  refIndex: ReadonlyMap<string, ParcelRecord>,
): MemberSearchEntry[] {
  return useMemo(
    () => buildMemberSearchEntries(members, parcelRefByMemberId, refIndex),
    [members, parcelRefByMemberId, refIndex],
  );
}
